import {
  collection,
  doc,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  Timestamp,
  serverTimestamp,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Task, TaskPriority, TaskStatus } from '@/types';

// ============================================
// Collection Path Helpers
// ============================================

// Note: Tasks use a root-level collection with userId field (not nested under users)
const getTasksRef = () => collection(db, 'tasks');
const getTaskRef = (taskId: string) => doc(db, 'tasks', taskId);

// ============================================
// Document Converters
// ============================================

export const docToTask = (docSnap: QueryDocumentSnapshot<DocumentData>): Task => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    userId: data.userId,
    title: data.title,
    notes: data.notes || '',
    estimatedDuration: data.estimatedDuration,
    dueDate: data.dueDate || null,
    priority: data.priority,
    categoryId: data.categoryId,
    status: data.status,
    scheduledBlockId: data.scheduledBlockId || null,
    order: data.order,
    isMIT: data.isMIT,
    mitDate: data.mitDate,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
};

// ============================================
// Input Types
// ============================================

export interface CreateTaskInput {
  title: string;
  notes?: string;
  estimatedDuration?: number;
  dueDate?: string | null;
  priority?: TaskPriority;
  categoryId?: string;
}

// ============================================
// CRUD Operations
// ============================================

export async function getTasks(userId: string): Promise<Task[]> {
  const tasksRef = getTasksRef();
  const tasksQuery = query(
    tasksRef,
    where('userId', '==', userId),
    orderBy('order', 'asc'),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(tasksQuery);
  return snapshot.docs.map(docToTask);
}

export async function createTask(
  userId: string,
  data: CreateTaskInput,
  maxOrder: number
): Promise<Task> {
  const now = Timestamp.now();
  const newTaskData = {
    userId,
    title: data.title.trim(),
    notes: data.notes?.trim() || '',
    estimatedDuration: data.estimatedDuration || 30,
    dueDate: data.dueDate || null,
    priority: data.priority || 'medium',
    categoryId: data.categoryId || 'default-0',
    status: 'pending' as TaskStatus,
    scheduledBlockId: null,
    order: maxOrder + 1,
    createdAt: now,
    updatedAt: now,
  };

  const tasksRef = getTasksRef();
  const docRef = await addDoc(tasksRef, newTaskData);

  return {
    id: docRef.id,
    ...newTaskData,
  };
}

export async function updateTask(taskId: string, updates: Partial<Task>): Promise<void> {
  const taskRef = getTaskRef(taskId);
  await updateDoc(taskRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteTask(taskId: string): Promise<void> {
  const taskRef = getTaskRef(taskId);
  await deleteDoc(taskRef);
}

// ============================================
// Batch Operations
// ============================================

export async function reorderTasks(tasks: { id: string; order: number }[]): Promise<void> {
  const batch = writeBatch(db);

  tasks.forEach((task) => {
    const taskRef = getTaskRef(task.id);
    batch.update(taskRef, {
      order: task.order,
      updatedAt: serverTimestamp(),
    });
  });

  await batch.commit();
}

// ============================================
// Convenience Operations
// ============================================

export async function completeTask(taskId: string): Promise<void> {
  await updateTask(taskId, { status: 'completed' });
}

export async function scheduleTask(taskId: string, blockId: string): Promise<void> {
  await updateTask(taskId, {
    status: 'scheduled',
    scheduledBlockId: blockId,
  });
}

export async function unscheduleTask(taskId: string): Promise<void> {
  await updateTask(taskId, {
    status: 'pending',
    scheduledBlockId: null,
  });
}
